// Fields for more than words: several email addresses at once, a file dropped or chosen, and a long list to choose
// from in a sheet with a search.
import { useEffect, useId, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Button, IconButton } from './Button';
import { cx } from './cx';
import s from './Pickers.module.css';
import fs from './Field.module.css';
import { FileArrowUpIcon, FileTextIcon, WarningCircleIcon, XIcon } from './icons';
import { ListChoice, ListSection } from './List';
import { SearchField } from './Field';
import { bytesText, isEmail, parseEmails } from './text';
import { Sheet } from './Sheet';

/**
 * Several email addresses: each becomes a chip as it's typed (after a comma, a space or Enter) or pasted. One that
 * isn't an address is marked, in words.
 */
export function EmailListField({
  label,
  emails,
  onChange,
  hint,
  error,
  most = 50,
  disabled = false,
}: {
  label: string;
  emails: readonly string[];
  onChange: (emails: string[]) => void;
  hint?: ReactNode;
  error?: ReactNode;
  most?: number;
  disabled?: boolean;
}) {
  const id = useId();
  const [text, setText] = useState('');
  const take = (more: string) => {
    const added = parseEmails(more, emails);
    if (added.length > 0) {
      onChange([...emails, ...added].slice(0, most));
    }
    setText('');
  };
  const bad = emails.filter((e) => !isEmail(e)).length;
  const invalid = Boolean(error) || bad > 0;
  const describedBy =
    [invalid ? `${id}-error` : null, hint ? `${id}-hint` : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx(fs.field, invalid && fs.invalid)}>
      <label htmlFor={id} className={fs.label}>
        {label}
      </label>
      <div className={fs.control}>
        <ul className={s.chips} role="list" aria-label={`${label}: ${emails.length} added`}>
          {emails.map((email) => (
            <li key={email} className={cx(s.chip, !isEmail(email) && s.bad)}>
              <span>
                {email}
                {!isEmail(email) && <span className="visually-hidden">, isn't an email address</span>}
              </span>
              <button
                type="button"
                aria-label={`Take out ${email}`}
                disabled={disabled}
                onClick={() => onChange(emails.filter((e) => e !== email))}
              >
                <XIcon size={14} weight="bold" aria-hidden="true" />
              </button>
            </li>
          ))}
          <li className={s.chipInput}>
            <input
              id={id}
              type="text"
              inputMode="email"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              disabled={disabled || emails.length >= most}
              placeholder={emails.length === 0 ? 'name@example.com' : 'Add another'}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={text}
              onChange={(e) => {
                const v = e.target.value;
                if (/[\s,;]$/.test(v)) {
                  take(v);
                } else {
                  setText(v);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && text.trim()) {
                  e.preventDefault();
                  take(text);
                } else if (e.key === 'Backspace' && text === '' && emails.length > 0) {
                  onChange(emails.slice(0, -1));
                }
              }}
              onPaste={(e) => {
                e.preventDefault();
                take(`${text} ${e.clipboardData.getData('text')}`);
              }}
              onBlur={() => text.trim() && take(text)}
            />
          </li>
        </ul>
      </div>
      {hint && (
        <p id={`${id}-hint`} className={fs.hint}>
          {hint}
        </p>
      )}
      {invalid && (
        <p id={`${id}-error`} className={fs.error}>
          <WarningCircleIcon size={16} weight="fill" aria-hidden="true" />
          <span>
            {error ?? (bad === 1 ? "One of these isn't an email address." : `${bad} of these aren't email addresses.`)}
          </span>
        </p>
      )}
    </div>
  );
}

/** A file dropped here, or chosen with the button. Once one is chosen, it shows, with a way to take it away. */
export function FileDrop({
  label,
  hint,
  accept,
  file,
  onFile,
  disabled = false,
}: {
  /** What it's for: "Add a config file". */
  label: string;
  /** What it takes: "A .tuneconfig file, up to 2 MB". */
  hint?: ReactNode;
  accept?: string;
  file: File | null;
  onFile: (file: File | null) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const id = useId();
  if (file) {
    return (
      <div className={s.chosen}>
        <FileTextIcon size={28} aria-hidden="true" />
        <div>
          <span className={s.chosenName}>{file.name}</span>
          <span className={s.chosenSize}>{bytesText(file.size)}</span>
        </div>
        <IconButton
          label="Choose another file"
          icon={<XIcon size={18} weight="bold" />}
          disabled={disabled}
          onClick={() => onFile(null)}
        />
      </div>
    );
  }
  const dropped = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files[0];
    if (f && !disabled) {
      onFile(f);
    }
  };
  return (
    <div
      className={cx(s.drop, over && s.over, disabled && s.disabled)}
      onDragEnter={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={() => setOver(false)}
      onDrop={dropped}
    >
      <FileArrowUpIcon className={s.dropIcon} size={36} aria-hidden="true" />
      <p className={s.dropTitle} id={`${id}-title`}>
        {label}
      </p>
      {hint && (
        <p className={s.dropHint} id={`${id}-hint`}>
          {hint}
        </p>
      )}
      <Button
        variant="gray"
        disabled={disabled}
        onClick={() => input.current?.click()}
        aria-describedby={`${id}-title ${hint ? `${id}-hint` : ''}`}
      >
        Choose a file
      </Button>
      <input
        ref={input}
        type="file"
        accept={accept}
        hidden
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) {
            onFile(f);
          }
        }}
      />
    </div>
  );
}

export interface PickerOption {
  value: string;
  label: string;
  detail?: string;
  /** Other words it's found by. */
  keywords?: string;
}

export interface PickerGroup {
  heading: string;
  options: readonly PickerOption[];
}

/** The most rows a search shows at once: more are found by searching closer. */
const MOST_SHOWN = 300;

/**
 * A long list to choose from, in a sheet: one (radios) or many (ticks), grouped, with a search. Nothing changes until
 * Done.
 */
export function PickerSheet({
  open,
  onClose,
  title,
  searchLabel,
  groups,
  selected,
  multiple = false,
  most,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  searchLabel: string;
  groups: readonly PickerGroup[];
  selected: readonly string[];
  multiple?: boolean;
  /** The most that can be ticked. */
  most?: number;
  onDone: (selected: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<string[]>([...selected]);
  const name = useId();
  // Each time it opens, it starts from what's chosen now.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setChosen([...selected]);
      setQuery('');
    }
    wasOpen.current = open;
  }, [open, selected]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out: PickerGroup[] = [];
    let left = MOST_SHOWN;
    for (const g of groups) {
      const matching = q
        ? g.options.filter((o) =>
            `${o.label} ${o.value} ${o.detail ?? ''} ${o.keywords ?? ''}`.toLowerCase().includes(q),
          )
        : g.options;
      const options = matching.slice(0, Math.max(left, 0));
      left -= options.length;
      if (options.length > 0) {
        out.push({ ...g, options });
      }
    }
    return out;
  }, [groups, query]);
  const total = groups.reduce((n, g) => n + g.options.length, 0);
  const listed = shown.reduce((n, g) => n + g.options.length, 0);
  const full = most !== undefined && chosen.length >= most;
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      size="large"
      action={{ label: 'Done', onClick: () => onDone(chosen) }}
    >
      <div className={s.pickerSearch}>
        <SearchField label={searchLabel} value={query} onValueChange={setQuery} />
      </div>
      {multiple && most !== undefined && (
        <p className={s.pickerNote} aria-live="polite">
          {chosen.length} of {most} chosen
        </p>
      )}
      {shown.map((g) => (
        <ListSection key={g.heading} header={g.heading} level={3}>
          {g.options.map((o) => (
            <ListChoice
              key={o.value}
              type={multiple ? 'checkbox' : 'radio'}
              name={name}
              label={o.label}
              subtitle={o.detail}
              checked={chosen.includes(o.value)}
              disabled={multiple && full && !chosen.includes(o.value)}
              onChange={(e) =>
                setChosen((c) =>
                  multiple ? (e.target.checked ? [...c, o.value] : c.filter((v) => v !== o.value)) : [o.value],
                )
              }
            />
          ))}
        </ListSection>
      ))}
      {listed === 0 && <p className={s.pickerNote}>Nothing matches “{query}”.</p>}
      {listed < total && listed >= MOST_SHOWN && <p className={s.pickerNote}>Search to find the rest.</p>}
    </Sheet>
  );
}
